import LINKS from 'src/_helpers/links'
import { DEFAULT_JOURNAL_DOMAIN_ROUTES } from 'src/_helpers/journalDomainRoutes'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'

//const getTranslation = (item, locale) => item?.translations?.[locale] || item?.translations?.pl || {}

export const buildPrefixedLabel = (label, prefix) => {
  if (!label || !prefix) return label

  const normalizedLabel = String(label).trim().toLocaleLowerCase()
  const normalizedPrefix = String(prefix).trim().toLocaleLowerCase()

  if (normalizedLabel.startsWith(normalizedPrefix)) return label

  return `${prefix} ${label}`
}

const buildIssueOptionLabel = (issue, locale, issuePrefix, volumeLabel, untitledLabel) => {
  const translation = getPreferredTranslation(issue, locale)
  const primaryLabel = issue?.number
    ? buildPrefixedLabel(String(issue.number), issuePrefix)
    : translation?.title || issue?.title || untitledLabel
  const details = [volumeLabel, issue?.releaseYear].filter(Boolean)

  return details.length ? `${primaryLabel} (${details.join(', ')})` : primaryLabel
}

export const buildVolumeMap = (volumes = []) => {
  return (Array.isArray(volumes) ? volumes : []).reduce((acc, volume) => {
    if (volume?.uuid) acc[volume.uuid] = volume
    return acc
  }, {})
}

export const buildVolumeTitle = (volume, locale, volumePrefix) => {
  if (!volume) return ''
  const translation = getPreferredTranslation(volume, locale)
  if (translation?.title) return translation.title
  if (volume?.title) return volume.title
  if (volume?.number && volumePrefix) return `${volumePrefix} ${volume.number}`
  return ''
}

export const buildIssueTitle = (issue, locale, issuePrefix, volumeTitle, untitledLabel) => {
  const number = issue?.number
  const year = issue?.releaseYear
  const numberLabel = number && issuePrefix ? `${issuePrefix} ${number}` : ''

  const parts = [numberLabel, volumeTitle].filter(Boolean)
  let title = parts.join(', ')
  if (year) title = title ? `${title}, ${year}` : String(year)

  if (!title) {
    const translation = getPreferredTranslation(issue, locale)
    title = translation?.title || issue?.title || untitledLabel
  }

  return title
}

export const buildIssueLabel = (issue, locale, issuePrefix, volumeTitle, untitledLabel) => {
  const title = buildIssueTitle(issue, locale, issuePrefix, volumeTitle, untitledLabel)
  return title
}

export const buildNumberVolumeOptions = ({
  issues = [],
  volumes = [],
  locale,
  issuePrefix,
  volumePrefix,
  untitledLabel,
}) => {
  const volumeMap = buildVolumeMap(volumes)
  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' })

  const volumeOptions = (Array.isArray(volumes) ? volumes : [])
    .map((volume) => {
      const label = buildPrefixedLabel(buildVolumeTitle(volume, locale, volumePrefix), volumePrefix)
      if (!label || !volume?.uuid) return null
      return { value: `volume:${volume.uuid}`, label }
    })
    .filter(Boolean)

  const issueOptions = (Array.isArray(issues) ? issues : [])
    .map((issue) => {
      if (!issue?.uuid) return null
      const volumeTitle = buildPrefixedLabel(
        buildVolumeTitle(volumeMap[issue.parentUuid], locale, volumePrefix),
        volumePrefix,
      )
      const label = buildIssueOptionLabel(issue, locale, issuePrefix, volumeTitle, untitledLabel)
      if (!label) return null
      return { value: `issue:${issue.uuid}`, label }
    })
    .filter(Boolean)

  const sortByLabel = (a, b) => collator.compare(a.label, b.label)

  return [...volumeOptions.sort(sortByLabel), ...issueOptions.sort(sortByLabel)]
}

const parseReleaseYear = (value) => {
  const normalized = String(value ?? '').trim()

  if (!/^\d+$/.test(normalized)) return null

  const year = Number(normalized)

  return Number.isSafeInteger(year) ? year : null
}

export const buildReleaseYearOptions = (issues = []) => {
  const years = (Array.isArray(issues) ? issues : [])
    .map((issue) => parseReleaseYear(issue?.releaseYear))
    .filter((year) => year !== null)

  if (years.length === 0) return []

  const lowestYear = Math.min(...years)
  const highestYear = Math.max(...years)

  return Array.from({ length: highestYear - lowestYear + 1 }, (_, index) => {
    const year = String(lowestYear + index)

    return { value: year, label: year }
  })
}

export const mapIssuesToTiles = (
  issues,
  {
    locale,
    journalSlug,
    volumeByUuid,
    issuePrefix,
    labels = {},
    routeContext = 'main',
    journalRoutes = DEFAULT_JOURNAL_DOMAIN_ROUTES,
  },
) => {
  const items = Array.isArray(issues) ? issues : []
  const issueDetailRoute = routeContext === 'journal-domain' ? journalRoutes.ISSUES.DETAIL : LINKS.ISSUES.DETAIL

  return items
    .map((issue) => {
      if (!issue) return null
      const translation = getPreferredTranslation(issue, locale)
      const volumeTitle = buildVolumeTitle(volumeByUuid?.[issue.parentUuid], locale, labels.volumePrefix)
      const title = buildIssueTitle(issue, locale, issuePrefix, volumeTitle, labels.untitled)
      const subtitle = translation?.subtitle || (translation?.title !== title ? translation?.title : '') || ''
      const issueSlug = translation?.slug || issue?.slug || issue?.number
      const listThumb = issue?.thumbs?.list

      const tags = []
      if (issue?.isCurrentIssue && labels.currentIssue) tags.push({ label: labels.currentIssue, type: 'default' })
      if (issue?.special && labels.specialIssue) tags.push({ label: labels.specialIssue, type: 'default' })
      //if (issue?.openAccess && labels.openAccess) tags.push({ label: labels.openAccess, type: 'default' })

      const metadata = []
      if (issue?.doi) metadata.push(`DOI ${issue.doi}`)

      const ctaHref = buildLocalizedPath(issueDetailRoute, locale, { slug: journalSlug, issue: issueSlug })

      return {
        id: issue?.uuid || issue?.id,
        title,
        subtitle,
        imageSrc: listThumb?.url ? getMediaUrl(listThumb.url) : null,
        imageAlt: translation?.frontCoverAlt || title,
        metadata,
        tags,
        ctaHref,
      }
    })
    .filter(Boolean)
}
